//#region Imports

// ** React imports
import { memo } from "react";

// ** MUI Imports
import {
  Stepper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  CardContent,
  Box,
  Grid,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CircularProgress from "@mui/material/CircularProgress";
import DepartmentDialog from "screens/Employees/component/Department";
import JobTitleDialog from "screens/Employees/component/Jobtitle";

// ** Custom Components Imports
import StepperWrapper from "components/styles/mui/stepper";

// ** Third Party Components
import Draggable from "react-draggable";
import _ from "lodash";

// ** Logic Imports
import { useAddEmployeeLogic } from "../logic/add-employee-logic";

import AddSkillTypeDialog from "screens/Settings/component/NewSkillType";
import AddRoleDialog from "screens/Settings/component/NewRole";

//#endregion Imports

//#region Styled Components

const StepperHeaderContainer = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    borderRight: 0,
    borderBottom: 'none',
  },
}));

const DraggablePaper = memo(function DraggablePaper(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
});

//#endregion Styled Components

const AddEmployeeDialog = ({ open, handleClose ,fetchEmployees}) => {
  const theme = useTheme();
  
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isXSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { AddEmployeeProps } = useAddEmployeeLogic({handleClose, fetchEmployees});

  const {
    activeStep,
    btnLoading,
    handleNext,
    handleBack,
    steps,
    memoizedSteps,
    getStepContent,
    SkillProps,
    navigate,
    resetForm,
    toast,
    RoleProps,
    departmentProps,
    departmentDialogOpen,
    setDepartmentDialogOpen,
    jobTitleDialogOpen, 
    setJobTitleDialogOpen,
    jobTitleProps,
  } = AddEmployeeProps;
 
  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={open}
        PaperComponent={isSmallScreen ? Paper : DraggablePaper}
        onClose={() => {
          handleClose();
          resetForm();
        }}
        aria-labelledby="form-dialog-title"
        PaperProps={{
          sx: {
            height: "100%",
            maxHeight: "95vh",
            width: isSmallScreen ? "95vw" : "auto",
            m: isSmallScreen ? 2 : 0,
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: isSmallScreen ? "flex-end" : "center",
          },
        }}
      >
        <DialogTitle
          style={{
            cursor: isSmallScreen ? "default" : "move",
            borderBottom: "1px solid #D9D9D9",
            padding: isSmallScreen ? theme.spacing(2) : theme.spacing(4),
          }}
          id="draggable-dialog-title"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: isSmallScreen ? 2 : 0,
            }}
          >
            <Typography variant="h3" fontWeight="bold">
              Create Employee
            </Typography>
            <StepperHeaderContainer
              sx={{
                width: isSmallScreen ? "100%" : "auto",
                pt: isSmallScreen ? 2 : 0,
              }}
            >
              <StepperWrapper>
                <Stepper
                  activeStep={activeStep}
                  connector={<></>}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: isSmallScreen ? "wrap" : "nowrap",
                    paddingTop: isSmallScreen ? "0" : "1.5rem !important",
                    gap: isSmallScreen ? "16px" : "16px",
                    "& .MuiStep-root": {
                      padding: isSmallScreen ? "8px" : "0",
                      flex: isSmallScreen ? "1 1 auto" : "none",
                    },
                  }}
                >
                  {memoizedSteps}
                </Stepper>
              </StepperWrapper>
            </StepperHeaderContainer>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            px: isSmallScreen ? 2 : 6,
            pt: isSmallScreen ? 2 : 4,
            pb: isSmallScreen ? 2 : 0,
          }}
        >
          <Box
            sx={{
              mx: isSmallScreen ? 0 : 6,
              mt: isSmallScreen ? 0 : 4,
              mb: isSmallScreen ? 2 : 0,
            }}
          >
            <form onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={isSmallScreen ? 2 : 5}>
                {getStepContent(activeStep)}
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: isSmallScreen ? "16px !important" : "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
            flexDirection: isSmallScreen ? "column-reverse" : "row",
            gap: isSmallScreen ? 2 : 0,
          }}
          className="dialog-actions-dense"
        >
          <Box sx={{ width: isSmallScreen ? "100%" : "auto" }}>
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                resetForm();
                handleClose(false);
              }}
              fullWidth={isSmallScreen}
            >
              Cancel
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              width: isSmallScreen ? "100%" : "auto",
              gap: isSmallScreen ? 2 : 0,
              flexDirection: isSmallScreen ? "column" : "row",
            }}
          >
            <Button
              variant="outlined"
              color="main"
              disabled={activeStep === 0}
              onClick={handleBack}
              fullWidth={isSmallScreen}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="main"
              sx={{ ml: isSmallScreen ? 0 : 5 }}
              onClick={handleNext}
              disabled={btnLoading}
              fullWidth={isSmallScreen}
              {...(btnLoading
                ? {
                    endIcon: (
                      <CircularProgress
                        size={20}
                        thickness={4}
                        sx={{ color: "inherit" }}
                      />
                    ),
                  }
                : {})}
            >
              {activeStep === steps.length - 1 ? "Save Employee" : "Next Step"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <AddSkillTypeDialog {...SkillProps} navigate={navigate} toast={toast} />
      <AddRoleDialog
        open={RoleProps.openRoleDlg}
        handleClose={RoleProps.handleCloseRoleDlg}
        RoleProps={RoleProps}
      />
        <DepartmentDialog
               open={departmentDialogOpen}
               handleClose={() => setDepartmentDialogOpen(false)}
               DepartmentProps={departmentProps}
             />

      <JobTitleDialog
      open={jobTitleDialogOpen}
      handleClose={() => setJobTitleDialogOpen(false)}
      JobTitleProps={jobTitleProps}
       />
    </>
  );
};

export default AddEmployeeDialog;
